/**
 * The ranked prospect list and the book's status, one page at a time.
 *
 * The previous page stays on screen while the next loads, so paging and filtering do not flash an
 * empty table; `isRefreshing` says the rows shown are that previous page. While a book is still
 * syncing, being scored, or has never been scored, the list polls, because the first scores arrive
 * in the background; once everything is settled there is nothing to watch.
 *
 * Errors are read into what the page should say: prospecting not available for this advisor (off, or
 * no consent to read the whole book), a temporary outage, or anything else. The server's own reason
 * is passed on when it gave one.
 *
 * Nothing is asked for until the page knows which CRM's book it is showing.
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { prospectingApi, queryKeys } from '@client/api/integrations';
import { ApiClientError } from '@client/api/apiClient';
import type { Prospecting } from '@client/api/types';

const PENDING_POLL_MS = 30_000;

/** What the API client says when the server gave no reason of its own. */
const NO_REASON_GIVEN = 'An error occurred interacting with the API.';

export type ProspectsUnavailable = 'notAvailable' | 'temporarilyUnavailable' | 'error';

export function useProspects(provider: Prospecting.Provider | null, query: Prospecting.ProspectListQuery) {
  const result = useQuery({
    queryKey: queryKeys.prospecting.prospects(provider ?? '', query as Record<string, unknown>),
    queryFn: () => prospectingApi.prospects.list(provider!, query),
    enabled: !!provider,
    placeholderData: keepPreviousData,
    refetchInterval: ({ state }) => {
      const book = state.data?.book;
      if (!book) return false;
      return book.scoring.pending || book.freshness.syncActive || !book.scoring.lastScoredAt ? PENDING_POLL_MS : false;
    },
  });

  const error = result.error;
  const unavailable: ProspectsUnavailable | null = !error
    ? null
    : error instanceof ApiClientError && error.status === 403
      ? 'notAvailable'
      : error instanceof ApiClientError && error.status === 503
        ? 'temporarilyUnavailable'
        : 'error';

  return {
    data: result.data,
    isLoading: !!provider && result.isLoading,
    isFetching: result.isFetching,
    /** The rows on screen are the previous page or filter's, shown while the one asked for loads. */
    isRefreshing: result.isPlaceholderData,
    unavailable,
    unavailableMessage: error?.message && error.message !== NO_REASON_GIVEN ? error.message : null,
    refetch: result.refetch,
  };
}
